/** @typedef {import('history').History} History */
/** @typedef {import('history').Location} Location */

/** Paths never used as `location.state.from` (open-redirect / auth hygiene). */
export const DEFAULT_FROM_BLOCKLIST = ['/', '/login', '/account/recover']

/**
 * True for in-app relative paths only (single leading slash, no protocol-relative URLs).
 * @param {unknown} path
 */
export function isSafeAppPath(path) {
  if (typeof path !== 'string') return false
  const trimmed = path.trim()
  if (!trimmed.startsWith('/') || trimmed.startsWith('//')) return false
  if (trimmed.includes('://')) return false
  return true
}

/** @param {unknown} path */
function normalizeAppPath(path) {
  if (typeof path !== 'string') return ''
  return path.trim()
}

/** @param {string} path */
export function pathnameOnly(path) {
  const normalized = normalizeAppPath(path)
  const idx = normalized.indexOf('?')
  return idx === -1 ? normalized : normalized.slice(0, idx)
}

/** @param {string} path @param {string[]} blocklist */
function isBlocklisted(path, blocklist) {
  const pn = pathnameOnly(path)
  return blocklist.some((entry) => {
    const blocked = normalizeAppPath(entry)
    return pn === blocked || pn === blocked.replace(/\/$/, '')
  })
}

/**
 * Whether the tab history stack has a previous entry (deep link / refresh → usually false).
 * @param {History} history
 */
export function canHistoryGoBack(history) {
  return Boolean(history && typeof history.length === 'number' && history.length > 1)
}

/**
 * Optional `location.state.from` — only used when there is nothing to pop (refresh, new tab).
 * @param {Location} location
 * @param {string[]} [blocklist]
 * @returns {string|null}
 */
export function getStateFromFallback(location, blocklist = DEFAULT_FROM_BLOCKLIST) {
  const from = location?.state?.from
  if (!isSafeAppPath(from)) return null
  if (isBlocklisted(from, blocklist)) return null
  return normalizeAppPath(from)
}

/**
 * Back navigation for PageHeader / cancel buttons.
 *
 * 1. `history.goBack()` when the stack has a prior entry (same as `go(-1)`).
 * 2. Else `push(state.from)` if the route was opened with a safe `from` (deep link).
 * 3. Else `push` / `replace` `fallback`.
 *
 * Do not `push(from)` while history has entries — that duplicates routes and loops with goBack.
 *
 * @param {History} history
 * @param {Location} location
 * @param {{
 *   fallback: string,
 *   replaceOnFallback?: boolean,
 *   blocklist?: string[],
 *   onNavigate?: () => boolean | void,
 * }} options
 */
export function navigateBack(history, location, options) {
  const fallback = options?.fallback
  if (options?.onNavigate?.() === false) return
  if (canHistoryGoBack(history)) {
    history.goBack()
    return
  }
  const from = getStateFromFallback(location, options?.blocklist)
  if (from) {
    history.push(from)
    return
  }
  if (isSafeAppPath(fallback)) {
    if (options?.replaceOnFallback) history.replace(fallback)
    else history.push(fallback)
  }
}
